import { createReducer } from '@reduxjs/toolkit'
import { cloneDeep } from 'lodash'
import CampaignElementItem from '../../models/campaigns/element'
import { types } from '../../actions/campaigns/elements_selector_actions'

const updateItemsAttributes = (state, cIDs, attrs) => {
  state.elements = state.elements.map((el) => {
    if (cIDs.some((cID) => el.cID.isEqual(cID))) {
      // Create a new instance instead of mutating
      const newEl = CampaignElementItem.reInitCopy(el)
      newEl.update(attrs)
      return newEl
    }
    return el
  })
}

const makeItemsSelected = (state, ids, selected) => {
  return updateItemsAttributes(state, ids, { selected })
}

const updateItemImageList = (state, item, images) => {
  item = CampaignElementItem.reInitCopy(item)
  const elIndex = state.elements.findIndex((el) => el.cID.isEqual(item.cID))
  item.update({ images })
  state.elements[elIndex] = item
}

const mergeItemImages = (state, item, images) => {
  let itemImages = cloneDeep(item.images)
  const newImages = images.reduce((result, img) => {
    const itemImgIndex = itemImages.findIndex((i) => i.id == img.id)
    const itemImg = itemImgIndex != -1 ? itemImages[itemImgIndex] : null
    if (itemImg) {
      itemImages[itemImgIndex] = { ...cloneDeep(itemImg), ...img }
    } else {
      result.push(img)
    }
    return result
  }, [])

  itemImages = itemImages.concat(newImages)
  return updateItemImageList(state, item, itemImages)
}

const addNewImageToItem = (state, item, data) => {
  const images = cloneDeep(item.images).concat(data)
  return updateItemImageList(state, item, images)
}

const updateItemImage = (state, item, data) => {
  const images = cloneDeep(item.images)
  const imgIndex = item.images.findIndex((img) => img.id == data.id)
  if (imgIndex !== -1) {
    images[imgIndex] = { ...images[imgIndex], ...data }
  }
  return updateItemImageList(state, item, images)
}

const removeItem = (state, item) => {
  const { elements } = state

  if (item.isCopy) {
    state.elements = item.deleteChild(elements)
  } else {
    if (item.descendantsNum) {
      state.elements = item.deleteChildren(elements)
    }
    makeItemsSelected(state, [item.cID], false)
  }
}

const makeItemCopy = (state, item) => {
  const copyItem = item.clone()
  const elIndex = state.elements.findIndex((el) => el.cID.isEqual(item.cID))
  const insertPosition = item.calcPositionForCopy(elIndex)
  state.elements.splice(insertPosition, 0, copyItem)
}

const removeItemImage = (state, item, imgId) => {
  const images = cloneDeep(item.images)
  const index = images.findIndex((img) => img.id == imgId)
  if (Number.isNaN(imgId)) {
    images.splice(index, 1)
  } else {
    images[index]._destroy = true
  }
  return updateItemImageList(state, item, images)
}

export default createReducer({}, (builder) => {
  builder
    .addCase(types.INIT_ELEMENTS, (state, action) => {
      const { id, step, tags, elementFormData, isReady, showAdditions, showImages, visibleColumns } = action
      const elements = CampaignElementItem.createList(action.elements)
      return {
        elements,
        id,
        step,
        tags: tags || [],
        elementFormData: elementFormData || {},
        isReady,
        activeItem: null,
        showAdditions: showAdditions || false,
        showImages: showImages || false,
        visibleColumns: visibleColumns || [],
      }
    })
    .addCase(types.SELECT_ITEMS, (state, action) => {
      makeItemsSelected(state, action.ids, true)
    })
    .addCase(types.SELECT_TAGS, (state, action) => {
      const { item } = action
      state.activeItem = null
      updateItemsAttributes(state, [item.cID], { tags: action.tags })
    })
    .addCase(types.SET_TAG_AMOUNT, (state, action) => {
      const { item } = action
      const newTagsAmounts = {
        ...item.tagsAmounts,
        [action.tagId]: action.amount,
      }
      updateItemsAttributes(state, [item.cID], { tagsAmounts: newTagsAmounts })
    })
    .addCase(types.REMOVE_ITEM, (state, action) => {
      const { item } = action
      removeItem(state, item)
    })
    .addCase(types.REMOVE_ITEM_WITH_TAGS, (state, action) => {
      const { item } = action
      removeItem(state, item)
    })
    .addCase(types.UPDATE_ITEM, (state, action) => {
      const { item } = action
      const attrs = { [action.attr]: action.value }
      updateItemsAttributes(state, [item.cID], attrs)
    })
    .addCase(types.ASSIGN_ACTIVE_ITEM, (state, action) => {
      const { item } = action
      state.activeItem = item
    })
    .addCase(types.ADD_NEW_IMAGE_TO_ITEM, (state, action) => {
      const { item, data } = action
      addNewImageToItem(state, item, data)
    })
    .addCase(types.UPDATE_ITEM_IMAGE, (state, action) => {
      const { item, data } = action
      updateItemImage(state, item, {
        ...data,
        src: data.url,
      })
    })
    .addCase(types.UPDATE_ITEM_IMAGE_LIST, (state, action) => {
      const { item } = action
      updateItemImageList(state, item, action.images)
    })
    .addCase(types.MERGE_ITEM_IMAGES, (state, action) => {
      const { item } = action
      mergeItemImages(state, item, action.images)
    })
    .addCase(types.REMOVE_ITEM_IMAGE, (state, action) => {
      const { item } = action
      removeItemImage(state, item, action.imageId)
    })
    .addCase(types.MAKE_COPY, (state, action) => {
      const { item } = action
      makeItemCopy(state, item)
    })
    .addCase(types.SAVE_SELECTED, (state) => {
      // No-op
    })
    .addCase(types.CREATE_ELEMENT, (state, action) => {
      const newItem = new CampaignElementItem(action.data)
      state.elements.push(newItem)
    })
    .addCase(types.ADD_ALL_ELEMENTS, (state, action) => {
      state.elements = state.elements.map((el) => {
        if (action.isLocation == el.isLocation) {
          const newEl = CampaignElementItem.reInitCopy(el)
          newEl.update({ selected: true })
          return newEl
        }
        return el
      })
    })
})
